import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import SuccessToast from '../../../components/ui/SuccessToast';

const AddVehicleModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    make: '', model: '', year: '', registrationNumber: '',
    emissionClassification: '', isPrimary: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeKey, setShakeKey] = useState(0);

  const emissionOptions = [
    { value: 'ev', label: 'Elektrisk (EV)', description: '50% rabatt på bomavgifter' },
    { value: 'hybrid', label: 'Hybrid', description: '30% rabatt på bomavgifter' },
    { value: 'euro6', label: 'Euro 6', description: '10% rabatt på bomavgifter' },
    { value: 'standard', label: 'Standard', description: 'Ingen rabatt' }
  ];

  const currentYear = new Date()?.getFullYear();
  const yearOptions = Array.from({ length: 30 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i)
  }));

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.make?.trim()) newErrors.make = 'Merke er påkrevd';
    if (!formData?.model?.trim()) newErrors.model = 'Modell er påkrevd';
    if (!formData?.year) newErrors.year = 'Årsmodell er påkrevd';
    if (!formData?.registrationNumber?.trim()) newErrors.registrationNumber = 'Registreringsnummer er påkrevd';
    if (!formData?.emissionClassification) newErrors.emissionClassification = 'Utslippsklasse er påkrevd';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) {
      setShakeKey(k => k + 1);
      return;
    }
    setIsSubmitting(true);
    await onAdd(formData);
    setTimeout(() => {
      setIsSubmitting(false);
      setShowToast(true);
      setFormData({ make: '', model: '', year: '', registrationNumber: '', emissionClassification: '', isPrimary: false });
      setTimeout(() => handleClose(), 600);
    }, 1000);
  };

  if (!isOpen && !isClosing) return null;

  const selectedEmission = emissionOptions?.find(opt => opt?.value === formData?.emissionClassification);

  return (
    <div className={`fixed inset-0 z-modal flex items-center justify-center p-4 md:p-6 ${isClosing ? 'modal-backdrop-exit' : 'modal-backdrop-enter'}`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(20, 24, 28, 0.8)', backdropFilter: 'blur(12px)' }}
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* Modal */}
      <div
        key={shakeKey > 0 ? `shake-${shakeKey}` : 'modal'}
        className={`relative w-full max-w-xl overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto scrollbar-custom ${isClosing ? 'modal-spring-exit' : 'modal-spring-enter'} ${shakeKey > 0 ? 'form-shake' : ''}`}
        style={{
          background: 'linear-gradient(160deg, rgba(58, 65, 71, 0.98) 0%, rgba(47, 53, 59, 0.99) 100%)',
          border: '1px solid rgba(160, 170, 178, 0.18)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(74, 144, 226, 0.08)'
        }}
      >
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, #4A90E2, #7B68EE, #4ECDC4)' }} />

        {/* Modal header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-7 py-5"
          style={{
            background: 'rgba(58, 65, 71, 0.95)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(160, 170, 178, 0.1)'
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-9 h-9 rounded-xl"
              style={{ background: 'rgba(74, 144, 226, 0.12)', border: '1px solid rgba(74, 144, 226, 0.2)' }}
            >
              <Icon name="Plus" size={17} color="#4A90E2" />
            </div>
            <div>
              <h3 style={{ fontFamily: 'Lora, serif', fontSize: '1.0625rem', fontWeight: 600, color: '#F4F7F6' }}>Legg til kjøretøy</h3>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#A0AAB2', marginTop: '1px' }}>Registrer nytt kjøretøy i din profil</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-smooth"
            style={{ background: 'rgba(160, 170, 178, 0.1)', border: '1px solid rgba(160, 170, 178, 0.15)', color: '#A0AAB2', cursor: 'pointer' }}
            aria-label="Lukk modal"
          >
            <Icon name="X" size={16} color="#A0AAB2" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-7 space-y-6">
          {/* Vehicle info */}
          <div
            className="p-5 rounded-2xl space-y-4"
            style={{ background: 'rgba(47, 53, 59, 0.5)', border: '1px solid rgba(160, 170, 178, 0.08)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Car" size={14} color="#4A90E2" />
              <span style={{ fontFamily: 'Lora, serif', fontSize: '0.9375rem', fontWeight: 600, color: '#F4F7F6' }}>Kjøretøyinformasjon</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Merke"
                  type="text"
                  placeholder="f.eks. Tesla, Volvo, BMW"
                  value={formData?.make}
                  onChange={(e) => { handleInputChange('make', e?.target?.value); setErrors(p => ({...p, make: ''})); }}
                  required
                />
                {errors?.make && <p className="error-msg-bounce" style={{ fontSize: '0.72rem', color: '#E85555', marginTop: '4px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{errors?.make}</p>}
              </div>
              <div>
                <Input
                  label="Modell"
                  type="text"
                  placeholder="f.eks. Model 3, XC90"
                  value={formData?.model}
                  onChange={(e) => { handleInputChange('model', e?.target?.value); setErrors(p => ({...p, model: ''})); }}
                  required
                />
                {errors?.model && <p className="error-msg-bounce" style={{ fontSize: '0.72rem', color: '#E85555', marginTop: '4px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{errors?.model}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Årsmodell"
                  placeholder="Velg år"
                  options={yearOptions}
                  value={formData?.year}
                  onChange={(value) => { handleInputChange('year', value); setErrors(p => ({...p, year: ''})); }}
                  searchable
                  required
                />
                {errors?.year && <p className="error-msg-bounce" style={{ fontSize: '0.72rem', color: '#E85555', marginTop: '4px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{errors?.year}</p>}
              </div>
              <div>
                <Input
                  label="Registreringsnummer"
                  type="text"
                  placeholder="AB12345"
                  value={formData?.registrationNumber}
                  onChange={(e) => { handleInputChange('registrationNumber', e?.target?.value?.toUpperCase()); setErrors(p => ({...p, registrationNumber: ''})); }}
                  description="Norsk registreringsnummer"
                  required
                />
                {errors?.registrationNumber && <p className="error-msg-bounce" style={{ fontSize: '0.72rem', color: '#E85555', marginTop: '4px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{errors?.registrationNumber}</p>}
              </div>
            </div>
          </div>

          {/* Emission classification */}
          <div
            className="p-5 rounded-2xl space-y-4"
            style={{ background: 'rgba(47, 53, 59, 0.5)', border: '1px solid rgba(160, 170, 178, 0.08)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Leaf" size={14} color="#4ECDC4" />
              <span style={{ fontFamily: 'Lora, serif', fontSize: '0.9375rem', fontWeight: 600, color: '#F4F7F6' }}>Utslippsklassifisering</span>
            </div>
            <Select
              label="Velg utslippsklasse"
              description="Dette påvirker rabattberettigelse på bomavgifter"
              placeholder="Velg klassifisering"
              options={emissionOptions}
              value={formData?.emissionClassification}
              onChange={(value) => { handleInputChange('emissionClassification', value); setErrors(p => ({...p, emissionClassification: ''})); }}
              required
            />
            {errors?.emissionClassification && <p className="error-msg-bounce" style={{ fontSize: '0.72rem', color: '#E85555', marginTop: '4px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{errors?.emissionClassification}</p>}
            {selectedEmission && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: 'rgba(78, 205, 196, 0.08)', border: '1px solid rgba(78, 205, 196, 0.2)' }}
              >
                <Icon name="BadgePercent" size={15} color="#4ECDC4" />
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', color: '#4ECDC4', fontWeight: 500 }}>
                  {selectedEmission?.description}
                </span>
              </div>
            )}
          </div>

          {/* Primary vehicle */}
          <div
            className="p-4 rounded-xl"
            style={{ background: 'rgba(47, 53, 59, 0.4)', border: '1px solid rgba(160, 170, 178, 0.08)' }}
          >
            <Checkbox
              label="Angi som primært kjøretøy"
              description="Dette kjøretøyet vil bli brukt som standard i bomkalkulator"
              checked={formData?.isPrimary}
              onChange={(e) => handleInputChange('isPrimary', e?.target?.checked)}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-smooth btn-spring"
              style={{
                background: 'linear-gradient(135deg, #F5A623, #E8941A)',
                color: '#1a1a1a',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
                boxShadow: '0 4px 20px rgba(245, 166, 35, 0.3)'
              }}
            >
              <Icon name={isSubmitting ? 'Loader2' : 'Plus'} size={15} color="#1a1a1a" />
              {isSubmitting ? 'Legger til...' : 'Legg til kjøretøy'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-smooth btn-spring"
              style={{
                background: 'rgba(160, 170, 178, 0.08)',
                color: '#A0AAB2',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid rgba(160, 170, 178, 0.15)',
                cursor: 'pointer'
              }}
            >
              Avbryt
            </button>
          </div>
        </form>
      </div>
      <SuccessToast
        visible={showToast}
        message="Kjøretøy lagt til!"
        subtext="Kjøretøyet er registrert i din profil."
        onDismiss={() => setShowToast(false)}
      />
    </div>
  );
};

export default AddVehicleModal;